import { Typography } from '@mui/material';

export enum MimeType {
  Pdf = 'application/pdf',
  Jpeg = 'image/jpeg',
  Png = 'image/png',
  Gif = 'image/gif',
  Bmp = 'image/bmp',
  Svg = 'image/svg+xml',
  Heic = 'image/heic',
  Webp = 'image/webp',
  Zip = 'application/zip',
  OctetStream = 'application/octet-stream',
  Doc = 'application/msword',
  Docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  Xls = 'application/vnd.ms-excel',
  Xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  Ppt = 'application/vnd.ms-powerpoint',
  Pptx = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  Pps = 'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
  Csv = 'text/csv',
  Txt = 'text/plain',
  Qfx = 'application/vnd.intu.qfx',
  Pages = 'application/vnd.apple.pages',
  Json = 'application/json',
  Xml = 'application/xml',
}

/** Extension → MIME map for all file types handled by the app. */
export const MIME_TYPE_BY_EXTENSION: Readonly<Record<string, MimeType>> = {
  pdf: MimeType.Pdf,
  jpg: MimeType.Jpeg,
  jpeg: MimeType.Jpeg,
  png: MimeType.Png,
  gif: MimeType.Gif,
  bmp: MimeType.Bmp,
  svg: MimeType.Svg,
  heic: MimeType.Heic,
  webp: MimeType.Webp,
  zip: MimeType.Zip,
  doc: MimeType.Doc,
  docx: MimeType.Docx,
  xls: MimeType.Xls,
  xlsx: MimeType.Xlsx,
  ppt: MimeType.Ppt,
  pptx: MimeType.Pptx,
  pps: MimeType.Pps,
  csv: MimeType.Csv,
  txt: MimeType.Txt,
  qfx: MimeType.Qfx,
  pages: MimeType.Pages,
  json: MimeType.Json,
  xml: MimeType.Xml,
};

/** MIME types allowed for file uploads in the app. */
export const ALLOWED_MIME_TYPES: MimeType[] = [
  MimeType.Jpeg,
  MimeType.Png,
  MimeType.Heic,
  MimeType.Webp,
  MimeType.Pptx,
  MimeType.Ppt,
  MimeType.Pps,
  MimeType.Xlsx,
  MimeType.Xls,
  MimeType.Docx,
  MimeType.Doc,
  MimeType.Pdf,
  MimeType.Csv,
  MimeType.Txt,
  MimeType.Qfx,
  MimeType.Pages,
];

export function getExtensionFromFilename(filename: string): string | undefined {
  if (!filename) return undefined;
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === filename.length - 1) {
    return undefined;
  }
  return filename.slice(lastDot + 1).toLowerCase();
}

export function getMimeTypeFromFilename(filename: string): MimeType {
  const ext = getExtensionFromFilename(filename);
  if (!ext) return MimeType.OctetStream;
  return MIME_TYPE_BY_EXTENSION[ext] ?? MimeType.OctetStream;
}

/** Maps a response Content-Type header value to a known MimeType, if recognized. */
export function getMimeTypeFromContentType(
  contentType: string,
): MimeType | undefined {
  const base = contentType.split(';')[0]?.trim().toLowerCase();
  if (!base || base === (MimeType.OctetStream as string)) {
    return undefined;
  }
  const knownTypes = Object.values(MimeType) as string[];
  return knownTypes.includes(base) ? (base as MimeType) : undefined;
}

function normalizeExtension(extension?: string | null): string | undefined {
  const normalized = extension?.replace(/^\./, '').trim().toLowerCase();
  return normalized || undefined;
}

/** True when the filename suffix is a known MIME extension or matches the supplied extension. */
function hasResolvableFileExtension(
  filename: string,
  suppliedExtension?: string | null,
): boolean {
  const ext = getExtensionFromFilename(filename);
  if (!ext) return false;

  const normalizedSupplied = normalizeExtension(suppliedExtension);
  if (normalizedSupplied && ext === normalizedSupplied) {
    return true;
  }

  return getMimeTypeFromFilename(filename) !== MimeType.OctetStream;
}

/**
 * Resolves a filename suitable for MIME lookup when display names omit extensions.
 * Prefers storage file names (e.g. Document2Record.FileName), then appends Extension.
 */
export function resolveFilenameForMimeLookup(
  displayName: string,
  options?: {
    fileName?: string | null;
    extension?: string | null;
  },
): string {
  const extension = normalizeExtension(options?.extension);
  const storageFileName = options?.fileName?.trim();
  if (
    storageFileName &&
    hasResolvableFileExtension(storageFileName, extension)
  ) {
    return storageFileName;
  }

  const baseName = storageFileName || displayName.trim() || 'file';

  if (extension) {
    if (hasResolvableFileExtension(baseName, extension)) {
      return baseName;
    }
    return `${baseName}.${extension}`;
  }

  return baseName;
}

export function assertMimeTypeMatchesFilename(
  filename: string,
  mimeType: MimeType,
): void {
  const expected = getMimeTypeFromFilename(filename);
  if (mimeType !== expected) {
    console.error(
      `[mimeTypes] MIME type mismatch: got "${mimeType}" for "${filename}" (expected "${expected}")`,
    );
  }
}

const MAX_FILE_SIZE = 50 * 1024 * 1024;

export const ALLOWED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'heic',
  'webp',

  'pptx',
  'ppt',
  'pps',
  'xlsx',
  'xls',
  'docx',
  'doc',
  'pdf',

  'csv',
  'txt',
  'qfx',
  'pages',
];

export type FileValidationError =
  | { kind: 'tooLarge' }
  | { kind: 'unsupportedType'; extension: string | undefined }
  | { kind: 'mimeNotAllowed' };

export const getFileValidationError = (
  file: File,
): FileValidationError | null => {
  if (file.size > MAX_FILE_SIZE) {
    return { kind: 'tooLarge' };
  }

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
    return { kind: 'unsupportedType', extension: ext };
  }

  const isAllowedMime = ALLOWED_MIME_TYPES.some(
    (allowed) => allowed.toLowerCase() === file.type.toLowerCase(),
  );
  const isStrictMime =
    file.type !== '' && file.type !== (MimeType.OctetStream as string);
  if (!isAllowedMime && isStrictMime) {
    return { kind: 'mimeNotAllowed' };
  }

  return null;
};

export const validateFile = (file: File): string | React.ReactNode | null => {
  const error = getFileValidationError(file);
  if (!error) {
    return null;
  }

  switch (error.kind) {
    case 'tooLarge':
      return (
        <Typography
          component='span'
          sx={{ fontSize: '13px' }}
        >
          This file is too large. The max file size is <strong>50 MB</strong>.
        </Typography>
      );
    case 'unsupportedType':
      return (
        <Typography
          component='span'
          sx={{ fontSize: '13px' }}
        >
          This file type <strong>(.{error.extension})</strong> isn’t supported.
          Please select a supported format.
        </Typography>
      );
    case 'mimeNotAllowed':
      return 'File MIME type not allowed.';
  }
};
